import { useCallback, useEffect, useState, type ReactNode } from "react";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/Warning";
import type { Theme } from "@mui/material";
import {
  Box,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { debounce } from "lodash-es";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  dayjs,
  getDataUriFromCsvResponse,
  NhsNumberSchema,
} from "@thalamos/common";
import type { Dayjs, ManipulateType } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  MhaDashboardDataParams,
  MhaDashboardDataResponse,
  MHAStatusType,
  PatientStateTableRow,
  PatientStateWithContext,
} from "@thalamos/aspire-common";
import { getActiveMhaStatus, MhaStatus } from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  Dropdown,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderWarningToast,
  TextField,
} from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import type { MultiSelectFilterPickerOption } from "~/components/form/MultiSelectFilterPicker.js";
import { MultiSelectFilterPicker } from "~/components/form/MultiSelectFilterPicker.js";
import { S132DashboardCell } from "~/components/s132/S132DashboardCell.js";
import { formatMhaStatus } from "~/components/util.js";
import { routeFns } from "~/routePaths.js";
import { ukLocalFormatDate, ukLocalFormatDateTime } from "~/util.js";

import "../../@types/index.js";

export type MhaStatusDashboardProps = {
  data?: MhaDashboardDataResponse;
  isLoading: boolean;
  error?: string;
  showNhsNumber: boolean;
  onS132Changed?: () => void;
};

export type ExpiryWarning = {
  showTime: boolean;
  warningThreshold: { unit: ManipulateType; value: number };
  criticalThreshold: { unit: ManipulateType; value: number };
};

export const expiryThresholdTimes: { [key in MhaStatus]?: ExpiryWarning } = {
  [MhaStatus.Section17A]: {
    warningThreshold: { unit: "weeks", value: 8 },
    criticalThreshold: { unit: "weeks", value: 2 },
    showTime: false,
  },
  [MhaStatus.Section3]: {
    warningThreshold: { unit: "weeks", value: 8 },
    criticalThreshold: { unit: "weeks", value: 1 },
    showTime: false,
  },
  [MhaStatus.Section2]: {
    warningThreshold: { unit: "days", value: 7 },
    criticalThreshold: { unit: "days", value: 3 },
    showTime: false,
  },
  [MhaStatus.Section4]: {
    warningThreshold: { unit: "hours", value: 48 },
    criticalThreshold: { unit: "days", value: 1 },
    showTime: true,
  },
  [MhaStatus.Section5_2]: {
    warningThreshold: { unit: "hours", value: 48 },
    criticalThreshold: { unit: "days", value: 1 },
    showTime: true,
  },
  [MhaStatus.Section5_4]: {
    warningThreshold: { unit: "hours", value: 3 },
    criticalThreshold: { unit: "hours", value: 1 },
    showTime: true,
  },
  [MhaStatus.Section136]: {
    warningThreshold: { unit: "hours", value: 6 },
    criticalThreshold: { unit: "hours", value: 2 },
    showTime: true,
  },
};

export type ExpiryClassification =
  "unknown" | "expired" | "critical" | "warning" | "normal";

/**
 * Classify an expiry date relative to "now" and the configured thresholds.
 * Pure function — no React/JSX dependencies.
 */
export function classifyExpiry(
  mhaStatus: MhaStatus,
  expiryDateTime: string | undefined,
  now: Dayjs,
  activeStateType?: MHAStatusType,
): ExpiryClassification {
  if (mhaStatus === MhaStatus.Incomplete) return "unknown";
  if (mhaStatus === MhaStatus.Expired) return "expired";
  if (!expiryDateTime) return "unknown";

  const expiryThreshold = expiryThresholdTimes[mhaStatus];
  if (!expiryThreshold) return "unknown";

  const expiryDate = dayjs(expiryDateTime);

  if (expiryDate.isBefore(now)) return "expired";

  if (
    activeStateType === "pending-renewal" ||
    activeStateType === "pending-cto"
  ) {
    return "normal";
  }

  if (
    expiryDate.isBefore(
      now.add(
        expiryThreshold.criticalThreshold.value,
        expiryThreshold.criticalThreshold.unit,
      ),
    )
  ) {
    return "critical";
  }

  if (
    expiryDate.isBefore(
      now.add(
        expiryThreshold.warningThreshold.value,
        expiryThreshold.warningThreshold.unit,
      ),
    )
  ) {
    return "warning";
  }

  return "normal";
}

function expiryCellContents(
  theme: Theme,
  state: PatientStateWithContext,
  activeStateType: MHAStatusType | undefined,
  expiryDateTime?: string,
) {
  const mhaStatus = state.state.mhaStatus.status;
  if (mhaStatus === MhaStatus.Incomplete)
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "orange", marginRight: "0.2em" }} />
        Unknown
      </Box>
    );

  if (mhaStatus === MhaStatus.Expired)
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "red", marginRight: "0.2em" }} />
        Unknown
      </Box>
    );

  if (!expiryDateTime) return "Unknown";

  const expiryThreshold = expiryThresholdTimes[mhaStatus];

  if (!expiryThreshold) return "Unknown";

  const expiryDate = dayjs(expiryDateTime);
  const now = dayjs();

  const formatFn = (d: Dayjs) =>
    expiryThreshold.showTime
      ? d.format("DD MMM YYYY HH:mm")
      : d.format("DD MMM YYYY");

  if (expiryDate.isBefore(now)) return `Expired (${formatFn(expiryDate)})`;

  const formattedDate = formatFn(expiryDate);

  const wasManuallyOverridden = state.expiryOverrideInfo;
  const message = wasManuallyOverridden
    ? `Expiry time manually overridden from ${ukLocalFormatDate(state.expiryOverrideInfo?.defaultExpiryDateTime!)}. Expiry time is end of day`
    : `Expiry time is end of day`;

  const expiryDateComponent = expiryThreshold.showTime ? (
    formattedDate
  ) : (
    <Tooltip title={message}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {formattedDate}{" "}
        {wasManuallyOverridden && (
          <Info
            sx={{
              color: theme.palette.primary.main,
              fontSize: "1.5rem",
              ml: 1,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );

  if (
    activeStateType === "pending-renewal" ||
    activeStateType === "pending-cto"
  ) {
    return expiryDateComponent;
  }

  if (
    expiryDate.isBefore(
      now.add(
        expiryThreshold.criticalThreshold.value,
        expiryThreshold.criticalThreshold.unit,
      ),
    )
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "red", marginRight: "0.2em" }} />
        {expiryDateComponent}
      </Box>
    );
  }

  if (
    expiryDate.isBefore(
      now.add(
        expiryThreshold.warningThreshold.value,
        expiryThreshold.warningThreshold.unit,
      ),
    )
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "orange", marginRight: "0.2em" }} />
        {expiryDateComponent}
      </Box>
    );
  }

  return expiryDateComponent;
}

// A Section 136 has no hospital — its location is a place of safety, recorded
// on the event under a key that varies by event type.
export function hospitalOrPosName(state: PatientStateWithContext): string {
  if (state.state.hospital?.name) return state.state.hospital.name;
  const ev = state.event as
    | {
        placeOfSafety?: { name?: string };
        section136Context?: { placeOfSafety?: { name?: string } };
        previousPlaceOfSafety?: { name?: string };
      }
    | undefined;
  return (
    ev?.placeOfSafety?.name ??
    ev?.section136Context?.placeOfSafety?.name ??
    ev?.previousPlaceOfSafety?.name ??
    "-"
  );
}

export function MhaStatusDashboardRow({
  index,
  row,
  showNhsNumber,
  showS132,
  onS132Changed,
}: {
  row: PatientStateTableRow;
  index: number;
  showNhsNumber: boolean;
  showS132?: boolean;
  onS132Changed?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  // A dual-status patient carries the Section 136 plus another detention.
  // Show the non-136 detention first then the Section 136, matching the
  // patient banner's ordering. Single-status rows render one value per cell.
  const states = [row.state, ...(row.additionalStates ?? [])].sort((a, b) => {
    const a136 = a.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    const b136 = b.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    return a136 - b136;
  });

  const isDeceasedPatient = ["formal", "informal"].includes(
    row.patientInfo.deathNotificationStatus ?? "",
  );

  const stackSx = { display: "flex", flexDirection: "column", gap: 1 } as const;

  return (
    <TableRow
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.common.paleBlue : "white",
      }}
    >
      <TableCell>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Link
            style={{
              textDecoration: "none",
            }}
            to={routeFns.patientHome(row.rootPatientId)}
          >
            <Typography
              sx={{
                textDecoration: "none",
                fontSize: "0.9375rem",
                backgroundColor: "transparent",
                outline: "none",
                color: "blue",
              }}
              variant="subtitle1"
            >
              {`${row.patientInfo.name.family.toUpperCase()}, ${row.patientInfo.name.given}`}
            </Typography>
          </Link>
          {isDeceasedPatient && (
            <TooltipInfoBox
              title={
                "This patient has been marked as deceased in NHS national systems"
              }
              icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
            />
          )}
        </Box>
      </TableCell>
      {showNhsNumber && (
        <TableCell>
          {NhsNumberSchema.catch(t("common.unknown")).parse(
            row.patientInfo.nhsNumber,
          )}
        </TableCell>
      )}
      <TableCell>
        {row.patientInfo.dateOfBirth
          ? dayjs(row.patientInfo.dateOfBirth).format("DD MMM YYYY")
          : "Unknown"}
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {states.map((s) => {
            const activeState = getActiveMhaStatus(
              s.state,
              dayjs().toISOString(),
            );
            return (
              <Box key={s.id}>
                {formatMhaStatus(activeState.state, activeState.type)}
              </Box>
            );
          })}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {states.map((s) => {
            const activeState = getActiveMhaStatus(
              s.state,
              dayjs().toISOString(),
            );
            return (
              <Box key={s.id}>
                {expiryCellContents(
                  theme,
                  s,
                  activeState?.type,
                  s.state.mhaStatus.expiryDateTime,
                )}
              </Box>
            );
          })}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {states.map((s) => (
            <Box key={s.id}>{hospitalOrPosName(s)}</Box>
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {states.map((s) => (
            <Box key={s.id}>{s.state.trust?.name ?? "-"}</Box>
          ))}
        </Box>
      </TableCell>
      {showS132 && (
        <TableCell>
          <S132DashboardCell
            patientId={row.rootPatientId}
            patientName={`${row.patientInfo.name.family.toUpperCase()}, ${row.patientInfo.name.given}`}
            statuses={row.s132 ?? []}
            onChanged={() => {
              onS132Changed?.();
            }}
          />
        </TableCell>
      )}
      <TableCell>
        <a href={routeFns.patientHome(row.rootPatientId)}>
          <Button label="View" />
        </a>
      </TableCell>
    </TableRow>
  );
}

const reportDownloadReasons = [
  { value: "reconciliationWithEpr", label: "Reconciliation with EPR" },
  { value: "externalInspection", label: "External Inspection" },
  {
    value: "other",
    label: "Other",
  },
];

function ReportDownloadDialog(props: {
  params: Omit<MhaDashboardDataParams, "limit" | "offset">;
  patientSearchFilter: string;
  onClose: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [reasonDescription, setReasonDescription] = useState<string | null>(
    null,
  );
  const theme = useTheme();

  return (
    <PopupDialog open={true} onClose={props.onClose}>
      <PopupDialogTitle
        closeDialog={props.onClose}
        titleText="Confirm MHA Status Report Download"
      />
      <Box>
        <Paper
          sx={{
            backgroundColor: theme.palette.grey[100],
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Included Statuses
                </TableCell>
                <TableCell>
                  {props.params.statuses
                    .map((status) => {
                      return formatMhaStatus(status, "active");
                    })
                    .join(", ")}
                </TableCell>{" "}
              </TableRow>
              <TableRow sx={{ fontWeight: "bold" }}>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Status Expiry Less Than
                </TableCell>
                <TableCell>
                  {props.params.expiryLessThan
                    ? props.params.expiryLessThan
                    : "Not specified"}
                </TableCell>
              </TableRow>
              <TableRow sx={{ fontWeight: "bold" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Trust Filter</TableCell>
                <TableCell>
                  {props.params.trustIds?.length
                    ? `${String(props.params.trustIds.length)} trust(s) selected`
                    : "Not specified"}
                </TableCell>
              </TableRow>
              <TableRow sx={{ fontWeight: "bold" }}>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Hospital Filter
                </TableCell>
                <TableCell>
                  {props.params.hospitalIds?.length
                    ? `${String(props.params.hospitalIds.length)} hospital(s) selected`
                    : "Not specified"}
                </TableCell>
              </TableRow>
              <TableRow sx={{ fontWeight: "bold" }}>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Patient Name Filter
                </TableCell>
                <TableCell>
                  {props.patientSearchFilter
                    ? props.patientSearchFilter
                    : "Not specified"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
        <Box sx={{ mt: "1em" }}>
          <FormLabel sx={{ fontWeight: "bold", marginTop: "1em" }}>
            Reason for download
          </FormLabel>
          <Select
            fullWidth
            value={reason}
            label="reason"
            onChange={(e) => {
              setReason(e.target.value);
              setReasonDescription(null);
            }}
          >
            {reportDownloadReasons.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {reason === "other" && (
          <Box sx={{ mt: "1em" }}>
            <TextField
              useFullWidth
              name="reasonDescription"
              label="Reason description"
              value={reasonDescription ?? ""}
              onChange={(e) => {
                setReasonDescription(e);
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1em",
          }}
        >
          <Button
            onClick={() => {
              props.onClose();
            }}
            label="Close"
            color="error"
          />
          <Button
            onClick={async () => {
              setIsDownloading(true);
              try {
                const response = await api.patientState.csvReport(
                  {
                    patientSearchFilter: props.patientSearchFilter,
                    reason: reason ?? "",
                    reasonDescription: reasonDescription ?? undefined,
                  },
                  props.params.statuses,
                  props.params.expiryLessThan,
                  props.params.trustIds,
                  props.params.hospitalIds,
                );
                setIsDownloading(false);
                if (response.status === 200) {
                  if (response.data) {
                    const dataUri = getDataUriFromCsvResponse(response.data);
                    triggerDownload(
                      dataUri,
                      `emha-status-report-${ukLocalFormatDateTime(dayjs().toISOString())}.csv`,
                    );
                  } else {
                    renderWarningToast({
                      message:
                        "Report was empty - no matching rows found. Please check filters and try again",
                    });
                  }
                  props.onClose();
                } else {
                  renderErrorToast({ message: "Failed to download report" });
                }
              } catch {
                setIsDownloading(false);
                renderErrorToast({ message: "Failed to download report" });
              }
            }}
            disabled={
              isDownloading ||
              !reason ||
              (reason === "other" && !reasonDescription)
            }
            label="Download"
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}

export function MhaStatusSearchBarControls(props: {
  params: Omit<MhaDashboardDataParams, "limit" | "offset">;
  patientSearchFilter: string;
  setPatientSearchFilter: (value: string) => void;
  updateParams: (
    params: Omit<MhaDashboardDataParams, "limit" | "offset">,
  ) => void;
  title: string;
  subTitle: ReactNode;
  showReportDownload: boolean;
  section136Enabled: boolean;
  availableTrusts: MultiSelectFilterPickerOption[];
  hospitalOptions: MultiSelectFilterPickerOption[];
}) {
  const patientSearchFilter = props.patientSearchFilter;
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const updateFn = useCallback(debounce(props.updateParams, 500), []);

  useEffect(() => {
    updateFn({ ...props.params, patientSearchFilter });
  }, [JSON.stringify(props.params), patientSearchFilter]);

  const handleTrustChange = useCallback(
    (trusts: MultiSelectFilterPickerOption[]) => {
      const trustIds = trusts.length > 0 ? trusts.map((t) => t.id) : undefined;
      props.updateParams({ ...props.params, trustIds, hospitalIds: undefined });
    },
    [props.params, props.updateParams],
  );

  const handleHospitalChange = useCallback(
    (hospitals: MultiSelectFilterPickerOption[]) => {
      const hospitalIds =
        hospitals.length > 0 ? hospitals.map((h) => h.id) : undefined;
      props.updateParams({ ...props.params, hospitalIds });
    },
    [props.params, props.updateParams],
  );

  const [showReportDownloadDialog, setShowReportDownloadDialog] =
    useState(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: "1em",
          marginTop: "1em",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "0.75rem",
              color: theme.palette.common.blueGray,
            }}
          >
            {props.title}
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.palette.common.blueGray }}
          >
            {props.subTitle}
          </Typography>
        </Box>
        {props.showReportDownload && (
          <Button
            startIcon={ButtonIcon.download}
            label="Download as Spreadsheet"
            onClick={() => {
              setShowReportDownloadDialog(true);
            }}
            size="small"
            color="primary"
          />
        )}
        {showReportDownloadDialog && (
          <ReportDownloadDialog
            params={props.params}
            patientSearchFilter={patientSearchFilter}
            onClose={() => {
              setShowReportDownloadDialog(false);
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: isSmallScreen ? "stretch" : "center",
          flexWrap: "wrap",
          gap: isSmallScreen ? 0 : 2,
        }}
      >
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : "250px",
            marginBottom: isSmallScreen ? "1em" : 0,
          }}
        >
          <MultiSelectFilterPicker
            label="Filter by trust"
            options={props.availableTrusts}
            value={props.availableTrusts.filter((t) =>
              props.params.trustIds?.includes(t.id),
            )}
            onChange={handleTrustChange}
            testId="trustFilterPicker"
          />
        </Box>
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : "250px",
            marginBottom: isSmallScreen ? "1em" : 0,
          }}
        >
          <MultiSelectFilterPicker
            label="Filter by hospital"
            options={props.hospitalOptions}
            value={props.hospitalOptions.filter((h) =>
              props.params.hospitalIds?.includes(h.id),
            )}
            onChange={handleHospitalChange}
            testId="hospitalFilterPicker"
          />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: isSmallScreen ? 0 : "2rem" }} />
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : "200px",
            marginBottom: isSmallScreen ? "1em" : 0,
          }}
        >
          <TextField
            useFullWidth
            name="patientSearchFilter"
            label="Filter by patient"
            value={patientSearchFilter}
            onChange={props.setPatientSearchFilter}
          />
        </Box>
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : "250px",
            marginBottom: isSmallScreen ? "1em" : 0,
          }}
        >
          <Dropdown
            label="Detention Status"
            name="statuses"
            renderedValue={`${String(props.params.statuses.length)} statuses selected`}
            multiple
            selectedValue={props.params.statuses}
            values={[
              { value: MhaStatus.Incomplete, label: "Unknown (Incomplete)" },
              { value: MhaStatus.Expired, label: "Unknown (Expired)" },
              { value: MhaStatus.Section5_2, label: "Section 5(2)" },
              { value: MhaStatus.Section5_4, label: "Section 5(4)" },
              { value: MhaStatus.Section2, label: "Section 2" },
              { value: MhaStatus.Section3, label: "Section 3" },
              { value: MhaStatus.Section4, label: "Section 4" },
              { value: MhaStatus.Section17A, label: "CTO" },
              ...(props.section136Enabled
                ? [{ value: MhaStatus.Section136, label: "Section 136" }]
                : []),
              { value: MhaStatus.NotDetained, label: "Not Detained" },
              { value: MhaStatus.Unknown, label: "Unknown" },
            ]}
            onChange={(newValues) => {
              // Don't allow us to end up with an empty list
              if (newValues.length > 0)
                props.updateParams({ ...props.params, statuses: newValues });
            }}
          />
        </Box>
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : "200px",
            marginBottom: isSmallScreen ? "1em" : 0,
          }}
        >
          <Dropdown
            label="Expiry Date"
            name="expiry"
            renderedValue={props.params.expiryLessThan || "-"}
            selectedValue={props.params.expiryLessThan}
            placeholder="Select a date range.."
            values={[
              { value: "", label: "-" },
              { value: "24 hours", label: "Expires in < 24 hours" },
              { value: "7 days", label: "Expires in < 7 days" },
              { value: "30 days", label: "Expires in < 30 days" },
              { value: "2 months", label: "Expires in < 2 months" },
            ]}
            onChange={(newValue) => {
              props.updateParams({
                ...props.params,
                expiryLessThan: newValue,
              });
            }}
          />
        </Box>
        <Box sx={{ minWidth: isSmallScreen ? 0 : "2rem" }} />
      </Box>
    </>
  );
}

export function MhaStatusDashboard(props: MhaStatusDashboardProps) {
  if (props.isLoading)
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginTop: "4em",
        }}
      >
        <LoadingSpinner />
      </Box>
    );

  // TODO: work this out
  if (props.error)
    return <Banner bannerType={BannerList.ERROR} title={props.error} />;

  // TODO: work this out
  if (!props.data?.rows.length)
    return <Banner bannerType={BannerList.INFO} title="No results found" />;

  const headerCellSx = { fontWeight: "bold" };
  // PatientStateTableRow.s132 is present only when recording is enabled, so its
  // presence decides the column.
  const showS132 = props.data.rows.some((row) => row.s132 !== undefined);

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableCell sx={headerCellSx}>Patient Name</TableCell>
            {props.showNhsNumber && (
              <TableCell sx={headerCellSx}>NHS No.</TableCell>
            )}
            <TableCell sx={headerCellSx}>Born</TableCell>
            <TableCell sx={headerCellSx}>MHA Status</TableCell>
            <TableCell sx={headerCellSx}>Expiry Date / Time</TableCell>
            <TableCell sx={headerCellSx}>Hospital</TableCell>
            <TableCell sx={headerCellSx}>Trust</TableCell>
            {showS132 && (
              <TableCell sx={headerCellSx}>S132 Information</TableCell>
            )}
            <TableCell sx={headerCellSx}></TableCell>
          </TableHead>
          <TableBody>
            {props.data.rows.map((row, index) => (
              <MhaStatusDashboardRow
                key={row.rootPatientId}
                row={row}
                index={index}
                showNhsNumber={props.showNhsNumber}
                showS132={showS132}
                onS132Changed={props.onS132Changed}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
